import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { inviteRef } from '../../utils/inviteRef';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
export const googleSignInEnabled = !!GOOGLE_CLIENT_ID;

// ── Two-factor handoff ──────────────────────────────────────────────────────
// /login is the only screen with the code step, but a 2FA account can be
// reached from three places: this panel's Google button (on /login, /signup and
// the modal) and the modal's own email form. Rather than duplicate that step
// three times, whoever gets { twoFactorRequired } parks the partialToken here
// and sends the browser to /login, which picks it up and opens on the step.
//
// sessionStorage because AuthModal lives outside the <Router> (see
// AuthModalContext) and leaves by a plain navigation, so router state isn't
// available; the URL is worse, since it would keep the token in history. The
// token is useless on its own anyway — it only redeems at /login/2fa together
// with a valid TOTP or backup code, and it expires on its own.
const TWO_FACTOR_KEY = 'uf.pendingTwoFactor';

export function handOffTwoFactor(partialToken) {
  try { sessionStorage.setItem(TWO_FACTOR_KEY, partialToken); } catch { /* storage blocked: /login falls back to asking for the password */ }
  const params = new URLSearchParams(window.location.search);
  // On /login and /signup the destination worth keeping is whatever returnTo
  // they arrived with. From the modal it's the page they're standing on —
  // which must not be an auth screen, or 2FA would land back on itself.
  const onAuthPage = /^\/(login|signup)\b/.test(window.location.pathname);
  const returnTo = params.get('returnTo')
    || (onAuthPage ? null : window.location.pathname + window.location.search);
  window.location.assign(returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login');
}

// Read-and-clear, so a stale token can't reopen the code step later.
export function takePendingTwoFactor() {
  try {
    const t = sessionStorage.getItem(TWO_FACTOR_KEY);
    sessionStorage.removeItem(TWO_FACTOR_KEY);
    return t || '';
  } catch { return ''; }
}

// Shared OAuth block — Google (live) + Apple/Facebook (stubbed) — used by
// both the AuthModal popup and the standalone /login and /signup pages so
// there's one place that knows how to load the Google Identity Services
// script and exchange the credential.
export default function AuthPanel({ mode, onSuccess, onError }) {
  const { setUser } = useAuth();
  const googleBtnRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;
    let cancelled = false;

    async function handleGoogleCredential(response) {
      try {
        const res = await fetch('/api/users/auth/google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ credential: response.credential, ref: inviteRef() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Google sign-in failed');
        // No session cookie was set — the account has 2FA on and still owes a
        // code. data.user isn't in this response, so setUser would blank it.
        if (data.twoFactorRequired) { handOffTwoFactor(data.partialToken); return; }
        setUser(data.user);
        onSuccess?.(data.user, data.isNewUser);
      } catch (err) {
        onError?.(err.message || 'Google sign-in failed.');
      }
    }

    function renderButton() {
      if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      googleBtnRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        shape: 'pill',
        width: 344,
        text: mode === 'signin' ? 'signin_with' : 'signup_with',
      });
    }

    if (window.google?.accounts?.id) {
      renderButton();
    } else {
      const existing = document.getElementById('google-identity-script');
      if (existing) {
        existing.addEventListener('load', renderButton, { once: true });
      } else {
        const script = document.createElement('script');
        script.id = 'google-identity-script';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.onload = renderButton;
        document.body.appendChild(script);
      }
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Not configured (local dev without the client ID): render nothing rather
  // than a disabled "coming soon" button. Callers hide their "or" divider
  // with googleSignInEnabled. Apple/Facebook follow the same rule.
  if (!GOOGLE_CLIENT_ID) return null;
  return (
    <div className="flex flex-col gap-3">
      <div ref={googleBtnRef} className="flex justify-center" />
    </div>
  );
}
